<div class="bg-white p-6 rounded-lg mb-6">
  <div class="flex justify-end mb-4" *ngIf="projectId && !isAwardedAccess">
    <button
      mat-flat-button
      color="primary"
      type="button"
      [routerLink]="['/home/edit-project', projectId]"
    >
      <mat-icon>edit</mat-icon>
      Edit Project
    </button>
  </div>

  <mat-tab-group fitInkBarToContent>
    <mat-tab label="Project Details">
      <section class="bg-white p-6 rounded-lg mb-6">
        <header class="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h1 class="font-semibold text-xl text-gray-900">
              {{ form.value.project_name || ('PROJECT_NAME' | translate) }}
            </h1>
            <p class="text-sm text-gray-500 mt-1">
              {{ 'Project Details' }}
              <span *ngIf="form.value.project_id" class="ml-2 text-gray-400">·</span>
              <span *ngIf="form.value.project_id" class="ml-2 text-gray-600">ID: {{ form.value.project_id }}</span>
            </p>
          </div>
          <div class="flex flex-wrap gap-2" *ngIf="form.value.domain || form.value.sub_domain">
            <span
              *ngIf="form.value.domain"
              class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-700"
            >
              {{ form.value.domain }}
            </span>
            <span
              *ngIf="form.value.sub_domain"
              class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700"
            >
              {{ form.value.sub_domain | titlecase }}
            </span>
          </div>
        </header>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div class="detail-item">
            <span class="detail-label">{{ 'ORG_NAME' | translate }}</span>
            <p class="detail-value">{{ displayValue(form.value.company_name) }}</p>
          </div>

          <div class="detail-item">
            <span class="detail-label">{{ 'companyCode' | translate }}</span>
            <p class="detail-value">{{ displayValue(form.value.company_code) }}</p>
          </div>

          <div class="detail-item" *ngIf="branchShow">
            <span class="detail-label">{{ 'BRANCH' | translate }}</span>
            <p class="detail-value">{{ getBranchName() }}</p>
          </div>

          <div class="detail-item">
            <span class="detail-label">{{ 'PROJECT_NAME' | translate }}</span>
            <p class="detail-value">{{ displayValue(form.value.project_name) }}</p>
          </div>

          <div class="detail-item">
            <span class="detail-label">{{ 'DOMAIN' | translate }}</span>
            <p class="detail-value">{{ displayValue(form.value.domain) }}</p>
          </div>

          <div class="detail-item" *ngIf="getSubDomains().length > 0">
            <span class="detail-label">{{ 'SUB DOMAIN' | translate }}</span>
            <p class="detail-value">{{ displayValue(form.value.sub_domain | titlecase) }}</p>
          </div>

          <!-- Legacy infrastructure type for Commercial / Utilities domains -->
          <div
            class="detail-item"
            *ngIf="form.value.domain === 'Commercial' || form.value.domain === 'Utilities'"
          >
            <span class="detail-label">{{ 'Sub Domain' | translate }}</span>
            <p class="detail-value">{{ displayValue(form.value.infrastructure_type) }}</p>
          </div>

          <!-- FSI (Construction domain only) -->
          <div class="detail-item" *ngIf="form.value.domain?.toLowerCase() === 'construction'">
            <span class="detail-label">{{ 'FSI (Floor Space Index)' | translate }}</span>
            <p class="detail-value">{{ displayValue(form.value.fsi) }}</p>
          </div>

          <!-- Domain-specific fields -->
          <ng-container *ngIf="domainFields[selectedDomain]">
            <ng-container *ngFor="let field of domainFields[selectedDomain]">
              <!-- Date fields -->
              <div class="detail-item" *ngIf="field.type === 'date' && form.get(field.key)">
                <span class="detail-label">{{ field.label | translate }}</span>
                <p class="detail-value">
                  <ng-container *ngIf="form.get(field.key)?.value as v; else dashTpl">
                    {{ v | date:'mediumDate' }}
                  </ng-container>
                </p>
              </div>

              <!-- Text fields -->
              <div class="detail-item" *ngIf="field.type === 'text' && form.get(field.key)">
                <span class="detail-label">{{ field.label | translate }}</span>
                <p class="detail-value">{{ displayValue(form.get(field.key)?.value) }}</p>
              </div>

              <!-- Numeric fields with unit display -->
              <div
                class="detail-item"
                *ngIf="(field.type === 'number' || field.type === 'volume' || field.type === 'weight' || field.type === 'distance' || field.type === 'area' || field.type === 'size' || field.type === 'duration' || field.type === 'percentage') && form.get(field.key)"
              >
                <span class="detail-label">{{ field.label | translate }}</span>
                <p class="detail-value">
                  <ng-container *ngIf="form.get(field.key)?.value as v; else dashTpl">
                    {{ v }}<ng-container *ngIf="field.type === 'percentage'">%</ng-container>
                    <span
                      class="detail-unit"
                      *ngIf="field.type !== 'number' && field.type !== 'percentage' && getFieldUnit(field.key) !== 'nos'"
                    >
                      {{ getFieldUnit(field.key) }}
                    </span>
                  </ng-container>
                </p>
              </div>
            </ng-container>
          </ng-container>

          <div class="detail-item">
            <span class="detail-label">{{ 'Size' | translate }}</span>
            <p class="detail-value">
              <ng-container *ngIf="form.value.size; else dashTpl">
                {{ form.value.size }} <span class="detail-unit">{{ getFieldUnit('size') }}</span>
              </ng-container>
            </p>
          </div>

          <div class="detail-item" *ngIf="getSizeInSqm() !== null">
            <span class="detail-label">{{ 'Size (in SQM)' | translate }}</span>
            <p class="detail-value">
              {{ getSizeInSqm() | number:'1.2-4' }} <span class="detail-unit">sqm</span>
            </p>
          </div>

          <div class="detail-item" *ngIf="!isAwardedAccess">
            <span class="detail-label">{{ 'Budget' | translate }}</span>
            <p class="detail-value">{{ displayValue(form.value.budget_amount) }}</p>
          </div>

          <div class="detail-item" *ngIf="!isAwardedAccess">
            <span class="detail-label">{{ 'Funding_Source' | translate }}</span>
            <p class="detail-value">{{ displayValue(form.value.funding_source) }}</p>
          </div>

          <div class="detail-item md:col-span-2 lg:col-span-3">
            <span class="detail-label">{{ 'Scope_Description' | translate }}</span>
            <p class="detail-value detail-value--multiline">
              {{ displayValue(form.value.scope_description) }}
            </p>
          </div>

          <div class="detail-item">
            <span class="detail-label">{{ 'Timeline_Start' | translate }}</span>
            <p class="detail-value">
              <ng-container *ngIf="form.value.timeline_start; else dashTpl">
                {{ form.value.timeline_start | date:'mediumDate' }}
              </ng-container>
            </p>
          </div>

          <div class="detail-item">
            <span class="detail-label">{{ 'Timeline_End' | translate }}</span>
            <p class="detail-value">
              <ng-container *ngIf="form.value.timeline_end; else dashTpl">
                {{ form.value.timeline_end | date:'mediumDate' }}
              </ng-container>
            </p>
          </div>

          <div class="detail-item">
            <span class="detail-label">{{ 'Permit_Status' | translate }}</span>
            <p class="detail-value">
              <ng-container *ngIf="form.value.permit_status; else dashTpl">
                <span
                  class="status-pill"
                  [ngClass]="{
                    'status-pill--approved': (form.value.permit_status || '').toLowerCase() === 'approved',
                    'status-pill--pending': (form.value.permit_status || '').toLowerCase() === 'pending'
                  }"
                >
                  {{ form.value.permit_status }}
                </span>
              </ng-container>
            </p>
          </div>

          <div class="detail-item">
            <span class="detail-label">Stage</span>
            <p class="detail-value">{{ displayValue(form.value.stage) }}</p>
          </div>
        </div>

        <!-- Road services -->
        <div class="mt-6" *ngIf="showRoadServices()">
          <p class="detail-label mb-2">{{ 'ROAD SERVICES' | translate }}</p>
          <div class="flex flex-wrap gap-2" *ngIf="getRoadServices().length > 0; else noRoadServices">
            <ng-container *ngFor="let service of getRoadServices()">
              <span
                *ngIf="isServiceSelected(service)"
                class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700"
              >
                {{ service }}
              </span>
            </ng-container>
          </div>
          <ng-template #noRoadServices>
            <p class="text-sm text-gray-500">—</p>
          </ng-template>
        </div>

        <ng-template #dashTpl>—</ng-template>
      </section>
    </mat-tab>
    <mat-tab label="Work Log">
        <app-add-work-project [embeddedProjectId]="projectId"></app-add-work-project>
    </mat-tab>
    <!-- <mat-tab label="Users">
      <app-add-user-project></app-add-user-project>
    </mat-tab> -->
    <mat-tab label="Staff allocation">
      <div class="p-4">
        <app-project-staff-allocation [projectId]="projectId" [projectName]="projectName || form.value.project_name"></app-project-staff-allocation>
      </div>
    </mat-tab>
    <mat-tab label="Machines">
      <div class="p-4">
        <app-project-machine-availability [projectId]="projectId"></app-project-machine-availability>
      </div>
    </mat-tab>
    <mat-tab label="Document">
      <app-document-list [embeddedProjectId]="projectId"></app-document-list>
    </mat-tab>

    <mat-tab label="Change History" *ngIf="!isAwardedAccess">
      <app-project-change-history [projectId]="projectId"></app-project-change-history>
    </mat-tab>
    
    <!-- Document Checklist Tab - Only for Construction/Road domains -->
    <mat-tab label="Approvals & Permits" *ngIf="showDocumentChecklist() && !isAwardedAccess">
      <div class="p-4">
        <app-construction-document-checklist
          [projectId]="projectId"
          [domain]="form.get('domain')?.value"
          [subDomain]="form.get('sub_domain')?.value"
          (documentUpdated)="onDocumentUpdated($event)">
        </app-construction-document-checklist>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
